import React from "react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { fmtMins } from "./sourceUi";

/**
 * The drill-down's chart vocabulary.
 *
 * Four shapes, all SVG, all theme-token driven, all sharing one set of rules so
 * the drawer reads as one instrument rather than four widgets:
 *
 *   · a baseline is always drawn — a bar chart without one is a shape, not a
 *     measurement;
 *   · one colour for tracked time — how activity reached us is internal
 *     plumbing, not something a reader should have to decode;
 *   · hover is a real affordance: the hovered value is promoted into the
 *     caption rather than hidden in a native `title` tooltip;
 *   · nothing is animated on data change — a bar that grows while you read it
 *     is a lie about the measurement.
 *
 * Every component degrades to a labelled empty state rather than rendering an
 * axis with nothing on it.
 */

/* ── shared bits ─────────────────────────────────────────────────────────── */

function ChartFrame({ t, caption, right, children, height }) {
  return (
    <div>
      {(caption || right) && (
        <div style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          gap: 10, marginBottom: 8, minHeight: 16,
        }}>
          <span style={{ fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.4 }}>{caption}</span>
          {right}
        </div>
      )}
      <div style={{ height, position: "relative" }}>{children}</div>
    </div>
  );
}

const Empty = ({ t, h, msg }) => (
  <div style={{
    height: h, display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: t.r.md, background: t.c.surfaceMuted,
    fontSize: 12, color: t.c.textTertiary, textAlign: "center", padding: "0 16px",
  }}>{msg}</div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   DaySpark — the hero's trailing day series.

   Deliberately not a line: the underlying quantity is a per-day total, and a
   line between two daily totals implies values that were never measured. Bars
   with a baseline are the honest encoding.
   ═══════════════════════════════════════════════════════════════════════════ */
export function DaySpark({ series = [], height = 40 }) {
  const t = useDashTheme();
  const [hover, setHover] = React.useState(null);

  const rows = (series || []).filter(Boolean);
  if (!rows.length) return null;

  const max = Math.max(...rows.map((d) => d.minutes || 0), 1);
  const active = hover != null ? rows[hover] : null;

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}
        onMouseLeave={() => setHover(null)}
      >
        {rows.map((d, i) => {
          const on = (d.minutes || 0) > 0;
          const h = on ? Math.max(3, Math.round(((d.minutes || 0) / max) * height)) : 3;
          return (
            <button
              key={d.dateLocal || i}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              aria-label={`${d.dateLocal}: ${fmtMins(d.minutes || 0)}`}
              style={{
                flex: 1, minWidth: 0, height: "100%", padding: 0, border: "none",
                background: "none", cursor: "default",
                display: "flex", alignItems: "flex-end",
              }}
            >
              <span style={{
                display: "block", width: "100%", height: h,
                borderRadius: "3px 3px 1px 1px",
                background: on ? t.c.primary : t.c.border,
                opacity: hover == null || hover === i ? 1 : 0.35,
                transition: "opacity 120ms",
              }} />
            </button>
          );
        })}
      </div>
      {/* The caption is the tooltip. A native title attribute on a 6px bar is
          unusable, and a floating tooltip inside a scrolling drawer clips. */}
      <div style={{
        marginTop: 6, fontSize: 10.5, color: t.c.textTertiary,
        display: "flex", justifyContent: "space-between", gap: 8,
        fontVariantNumeric: "tabular-nums", minHeight: 14,
      }}>
        {active ? (
          <>
            <span>{active.dateLocal}</span>
            <span style={{ color: t.c.text, fontWeight: 600 }}>
              {active.minutes ? fmtMins(active.minutes) : "nothing tracked"}
            </span>
          </>
        ) : (
          <>
            <span>{rows[0]?.dateLocal}</span>
            <span>{rows[rows.length - 1]?.dateLocal}</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HoursByDay — the full bar chart in the Hours tab.
   Gridlines behind, values above, source colour per bar, hovered bar promoted.
   ═══════════════════════════════════════════════════════════════════════════ */
export function HoursByDay({ series = [] }) {
  const t = useDashTheme();
  const [hover, setHover] = React.useState(null);

  const rows = (series || []).filter(Boolean);
  if (!rows.length) return <Empty t={t} h={180} msg="No tracked time in this window." />;

  const max = Math.max(...rows.map((d) => d.minutes || 0), 1);
  // Round the ceiling to a whole hour so the gridlines mean something.
  const ceil = Math.max(60, Math.ceil(max / 60) * 60);
  const plot = 150;
  const lines = [0, 0.5, 1].map((f) => ({ f, label: `${Math.round((ceil * f) / 60)}h` }));

  return (
    <ChartFrame
      t={t} height={plot + 46}
      caption={hover != null
        ? `${rows[hover].dateLocal} · ${rows[hover].minutes ? fmtMins(rows[hover].minutes) : "nothing tracked"}`
        : "One bar per day. Hover for the exact figure; a day with nothing tracked shows a dot on the baseline."}
    >
      {/* gridlines */}
      {lines.map((l) => (
        <div key={l.f} style={{
          position: "absolute", left: 28, right: 0, bottom: 26 + l.f * plot,
          borderTop: `1px ${l.f === 0 ? "solid" : "dashed"} ${t.c.border}`,
        }} />
      ))}
      {lines.map((l) => (
        <span key={`t${l.f}`} style={{
          position: "absolute", left: 0, bottom: 22 + l.f * plot,
          fontSize: 9.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
        }}>{l.label}</span>
      ))}

      <div
        style={{ position: "absolute", left: 28, right: 0, bottom: 0, top: 0, display: "flex", alignItems: "flex-end", gap: 5 }}
        onMouseLeave={() => setHover(null)}
      >
        {rows.map((d, i) => {
          const on = (d.minutes || 0) > 0;
          const h = on ? Math.max(3, Math.round(((d.minutes || 0) / ceil) * plot)) : 0;
          const dim = hover != null && hover !== i;
          return (
            <div
              key={d.dateLocal || i}
              onMouseEnter={() => setHover(i)}
              style={{
                flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "flex-end", gap: 5, cursor: "default",
              }}
            >
              {on ? (
                <span style={{
                  width: "100%", maxWidth: 34, height: h,
                  background: t.c.primary,
                  borderRadius: `${t.r.xs}px ${t.r.xs}px 2px 2px`,
                  opacity: dim ? 0.32 : 1, transition: "opacity 120ms",
                }} />
              ) : (
                // An untracked day is a dot on the baseline, not a missing bar —
                // absence has to be visible or the week looks shorter than it is.
                <span style={{ width: 4, height: 4, borderRadius: 99, background: t.c.borderStrong, marginBottom: 1 }} />
              )}
              <span style={{
                fontSize: 9.5, color: dim ? t.c.textTertiary : t.c.textSecondary,
                whiteSpace: "nowrap", height: 12,
              }}>{String(d.dateLocal || "").slice(5)}</span>
            </div>
          );
        })}
      </div>
    </ChartFrame>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   RollupBars — "where the effort went".

   Ranked horizontal bars. Width is share OF THE LARGEST row, not of the total:
   the question is "what dominated", and normalising to the total makes every
   row in a well-spread day look equally short.
   ═══════════════════════════════════════════════════════════════════════════ */
export function RollupBars({ rows = [], palette, onPick }) {
  const t = useDashTheme();
  const list = (rows || []).filter(Boolean);
  if (!list.length) return null;

  const colors = palette || [t.c.ai, t.c.primary, t.status.positive, t.status.warning, t.c.brand, t.status.info];

  return (
    <div>
      {list.map((r, i) => {
        const color = r.unattributed ? t.status.warning : colors[i % colors.length];
        const Tag = onPick ? "button" : "div";
        return (
          <Tag
            key={r.label || i}
            onClick={onPick ? () => onPick(r) : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "7px 4px",
              width: "100%", border: "none", background: "none", textAlign: "left",
              cursor: onPick ? "pointer" : "default", fontFamily: t.font.family, color: t.c.text,
              borderRadius: t.r.sm,
            }}
          >
            <span style={{
              fontSize: 12.5, flex: 1, minWidth: 0, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap",
              color: r.unattributed ? t.c.textTertiary : t.c.text,
              fontStyle: r.unattributed ? "italic" : "normal",
            }}>{r.label}</span>
            <span style={{
              width: 96, height: 6, borderRadius: 99, background: t.c.surfaceMuted,
              overflow: "hidden", flexShrink: 0,
            }}>
              <span style={{
                display: "block", height: "100%", borderRadius: 99,
                width: `${Math.max(3, r.share || 0)}%`, background: color,
              }} />
            </span>
            <span style={{
              fontSize: 11.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums",
              width: 54, textAlign: "right", flexShrink: 0,
            }}>{r.formatted}</span>
          </Tag>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HourStrip — 24 blocks, one day.

   Used both for a whole day (Timeline tab) and to place a single stretch on the
   clock (the focused takeaway). `highlight` dims everything outside a range so
   the reader sees WHEN, not just how much.
   ═══════════════════════════════════════════════════════════════════════════ */
export function HourStrip({ hours = [], highlight = null, onPick, selected = null, compact = false }) {
  const t = useDashTheme();
  const max = Math.max(...hours.map((h) => (h && h.trackedMinutes) || 0), 1);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 2.5 }}>
        {Array.from({ length: 24 }).map((_, hr) => {
          const h = hours.find((x) => x && x.hour === hr) || null;
          const on = h && !h.isEmpty && (h.trackedMinutes || 0) > 0;
          const inRange = !highlight || (hr >= highlight[0] && hr <= highlight[1]);
          const idleHeavy = on && (h.idleMinutes || 0) > (h.trackedMinutes || 0) / 2;
          const bg = !on ? t.c.surfaceMuted : idleHeavy ? t.status.warning : t.status.positive;
          const isSel = selected && h && selected.hourKey === h.hourKey;
          return (
            <button
              key={hr}
              disabled={!on || !onPick}
              onClick={on && onPick ? () => onPick(h) : undefined}
              title={`${String(hr).padStart(2, "0")}:00${on ? ` · ${fmtMins(h.trackedMinutes)}` : " · nothing tracked"}`}
              style={{
                height: compact ? 18 : 26, borderRadius: t.r.xs, border: "none", padding: 0,
                cursor: on && onPick ? "pointer" : "default", background: bg,
                opacity: !inRange ? 0.18 : on ? 0.42 + 0.58 * ((h.trackedMinutes || 0) / max) : 1,
                boxShadow: isSel ? `0 0 0 2px ${t.c.primary}` : "none",
                transition: "opacity 140ms",
              }}
            />
          );
        })}
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", marginTop: 5,
        fontSize: 9.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
      }}>
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SplitBar — active / idle, one row. Used in the hero.
   ═══════════════════════════════════════════════════════════════════════════ */
export function SplitBar({ activeMinutes = 0, idleMinutes = 0, height = 7 }) {
  const t = useDashTheme();
  const total = Math.max(1, activeMinutes + idleMinutes);
  const pct = Math.round((activeMinutes / total) * 100);
  return (
    <div style={{ display: "flex", height, borderRadius: 99, overflow: "hidden", background: t.c.surfaceMuted }}>
      <span style={{ width: `${pct}%`, background: t.status.positive }} />
      <span style={{ width: `${100 - pct}%`, background: t.status.warning }} />
    </div>
  );
}

const charts = { DaySpark, HoursByDay, RollupBars, HourStrip, SplitBar };
export default charts;
